import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaUsers, 
  FaDollarSign, 
  FaTrophy, 
  FaBaby, 
  FaSignOutAlt, 
  FaDownload,
  FaEye,
  FaSearch,
  FaFilter,
  FaCalendarAlt,
  FaChartBar,
  FaShieldAlt,
  FaChevronLeft,
  FaChevronRight,
  FaTimes,
  FaPhone,
  FaEnvelope,
  FaHome,
  FaUser,
  FaChild,
  FaTrash,
  FaCheck
} from 'react-icons/fa';
import { GiBarbecue } from 'react-icons/gi';
import './AdminDashboard.css';

const AdminDashboard = ({ user, onLogout }) => {
  const [dashboardData, setDashboardData] = useState(null);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [error, setError] = useState(null);
  
  
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  
  
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [showModal, setShowModal] = useState(false);
  
  // Confirmation modal state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmData, setConfirmData] = useState(null);
  
  const navigate = useNavigate();

  
  useEffect(() => {
    if (showModal) {
      
      const scrollY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    } else {
      
      const scrollY = document.body.style.top;
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }

    
    return () => {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
    };
  }, [showModal]);

  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      
      const [dashboardResponse, registrationsResponse] = await Promise.all([
        fetch('https://sogovia-cookoff-api-3bba922d210c.herokuapp.com/api/admin/dashboard-stats', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }),
        fetch('https://sogovia-cookoff-api-3bba922d210c.herokuapp.com/api/admin/registrations', {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })
      ]);

      
      if (!dashboardResponse.ok) {
        if (dashboardResponse.status === 401) {
          
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminUser');
          onLogout();
          navigate('/admin/login');
          return;
        }
        throw new Error(`Dashboard API error: ${dashboardResponse.status}`);
      }

      const dashboardResult = await dashboardResponse.json();
      
      if (dashboardResult.success) {
        setDashboardData(dashboardResult.data);
        console.log(dashboardResult.data);
      } else {
        throw new Error(dashboardResult.message || 'Failed to fetch dashboard data');
      }

      
      if (!registrationsResponse.ok) {
        if (registrationsResponse.status === 401) {
          
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminUser');
          onLogout();
          navigate('/admin/login');
          return;
        }
        throw new Error(`Registrations API error: ${registrationsResponse.status}`);
      }

      const registrationsResult = await registrationsResponse.json();
      
      if (registrationsResult.success) {
        setRegistrations(registrationsResult.data);
      } else {
        throw new Error(registrationsResult.message || 'Failed to fetch registrations data');
      }

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [onLogout, navigate]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    onLogout();
    navigate('/admin/login');
  };

  const exportData = () => {
    try {
      
      const csvData = filteredRegistrations.map(reg => ({
        'Registration ID': reg.id,
        'Team/Participant Name': reg.teamName,
        'Head Cook/Contact': reg.headCookName,
        'Email': reg.email,
        'Phone': reg.phone,
        'BBQ Team': reg.bbqTeam ? 'Yes' : 'No',
        'Kids Entries': reg.kidsEntries.length,
        'Jackpot Categories': Object.entries(reg.jackpotCategories)
          .filter(([_, value]) => value)
          .map(([key, _]) => key)
          .join(', '),
        'Total Amount': `$${reg.totalPayment}`,
        'Payment Status': reg.paymentStatus,
        'Registration Date': new Date(reg.registrationDate).toLocaleDateString(),
        'Kids Names': reg.kidsEntries.map(kid => kid.childName).join(', '),
        'Address': reg.address || 'N/A',
        'Comments': reg.comments || 'N/A'
      }));

      
      const headers = Object.keys(csvData[0] || {});
      const csvContent = [
        headers.join(','),
        ...csvData.map(row => 
          headers.map(header => {
            const value = row[header] || '';
            
            return value.toString().includes(',') ? `"${value.replace(/"/g, '""')}"` : value;
          }).join(',')
        )
      ].join('\n');

      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `bbq_registrations_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log('Registration data exported successfully');
    } catch (error) {
      console.error('Export failed:', error);
      alert('Failed to export data. Please try again.');
    }
  };

  const handleViewRegistration = (registration) => {
    setSelectedRegistration(registration);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedRegistration(null);
  };

  const showConfirmation = (action, data) => {
    setConfirmAction(action);
    setConfirmData(data);
    setShowConfirmModal(true);
  };

  const closeConfirmModal = () => {
    setShowConfirmModal(false);
    setConfirmAction(null);
    setConfirmData(null);
  };

  const handleConfirmAction = async () => {
    if (confirmAction && confirmData) {
      if (confirmAction === 'delete') {
        await deleteRegistrationAction(confirmData);
      } else if (confirmAction === 'markPaid') {
        await markAsPaidAction(confirmData);
      }
    }
    closeConfirmModal();
  };

  const handleDeleteRegistration = async (registrationId) => {
    const registration = registrations.find(reg => reg.id === registrationId);
    showConfirmation(
      'delete',
      {
        id: registrationId,
        title: 'Delete Registration',
        message: 'Are you sure you want to delete this registration?',
        details: `Registration: ${registration?.teamName || 'Unknown'}\nContact: ${registration?.headCookName || 'Unknown'}`,
        warning: 'This action cannot be undone.',
        confirmText: 'Delete',
        confirmClass: 'danger'
      }
    );
  };

  const deleteRegistrationAction = async (data) => {

    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Extract numeric ID if it's in REG0XX format
      const numericId = data.id.toString().startsWith('REG0') ? data.id.toString().replace('REG0', '') : data.id;

      const response = await fetch(`https://sogovia-cookoff-api-3bba922d210c.herokuapp.com/api/admin/registrations/${numericId}/remove`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminUser');
          onLogout();
          navigate('/admin/login');
          return;
        }
        throw new Error(`Failed to delete registration: ${response.status}`);
      }

      // Refresh the data after successful deletion
      await fetchDashboardData();
      alert('Registration deleted successfully');
      
    } catch (error) {
      console.error('Error deleting registration:', error);
      alert(`Failed to delete registration: ${error.message}`);
    }
  };

  const handleMarkAsPaid = async (registrationId) => {
    const registration = registrations.find(reg => reg.id === registrationId);
    showConfirmation(
      'markPaid',
      {
        id: registrationId,
        title: 'Mark as Paid',
        message: 'Are you sure you want to mark this registration as PAID?',
        details: `Registration: ${registration?.teamName || 'Unknown'}\nAmount: $${registration?.totalPayment || '0'}`,
        warning: 'This will update the payment status to "paid".',
        confirmText: 'Mark as Paid',
        confirmClass: 'success'
      }
    );
  };

  const markAsPaidAction = async (data) => {

    try {
      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('No authentication token found');
      }

      // Extract numeric ID if it's in REG0XX format
      const numericId = data.id.toString().startsWith('REG0') ? data.id.toString().replace('REG0', '') : data.id;

      const response = await fetch(`https://sogovia-cookoff-api-3bba922d210c.herokuapp.com/api/admin/registrations/${numericId}/mark-paid`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminUser');
          onLogout();
          navigate('/admin/login');
          return;
        }
        throw new Error(`Failed to mark as paid: ${response.status}`);
      }

      // Refresh the data after successful update
      await fetchDashboardData();
      
      // Update the selected registration if modal is open
      if (selectedRegistration && selectedRegistration.id === data.id) {
        setSelectedRegistration({
          ...selectedRegistration,
          paymentStatus: 'paid'
        });
      }
      
      alert('Registration marked as paid successfully');
      
    } catch (error) {
      console.error('Error marking as paid:', error);
      alert(`Failed to mark as paid: ${error.message}`);
    }
  };

  const filteredRegistrations = registrations.filter(reg => {
    const matchesSearch = reg.teamName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         reg.headCookName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         reg.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || reg.paymentStatus === filterStatus;
    
    return matchesSearch && matchesFilter;
  });

  
  const totalPages = Math.ceil(filteredRegistrations.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageRegistrations = filteredRegistrations.slice(startIndex, endIndex);

  
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="loading-spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-loading">
        <div className="admin-error">
          <h2>Error Loading Dashboard</h2>
          <p>{error}</p>
          <button onClick={fetchDashboardData} className="retry-btn">
            Retry
          </button>
          <button onClick={handleLogout} className="logout-btn">
              <FaSignOutAlt />
              Logout
            </button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      
      <header className="admin-header">
        <div className="admin-header-left">
          <FaShieldAlt className="admin-logo" />
          <div>
            <h1>Admin Dashboard</h1>
            <p>{dashboardData?.eventName}</p>
          </div>
        </div>
        <div className="admin-header-right">
          <div className="admin-user-info">
            <span>Welcome, {user?.name || 'Admin'}</span>
            <button onClick={handleLogout} className="logout-btn">
              <FaSignOutAlt />
              Logout
            </button>
          </div>
        </div>
      </header>

      
      <nav className="admin-nav">
        <button 
          className={`nav-tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <FaChartBar />
          Overview
        </button>
        <button 
          className={`nav-tab ${activeTab === 'registrations' ? 'active' : ''}`}
          onClick={() => setActiveTab('registrations')}
        >
          <FaUsers />
          Registrations
        </button>
        <button 
          className={`nav-tab ${activeTab === 'payments' ? 'active' : ''}`}
          onClick={() => setActiveTab('payments')}
        >
          <FaDollarSign />
          Payments
        </button>
      </nav>

      
      <main className="admin-main">
        {activeTab === 'overview' && (
          <div className="overview-section">
            
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">
                  <FaUsers />
                </div>
                <div className="stat-content">
                  <h3>{dashboardData?.totalRegistrations}</h3>
                  <p>Total Registrations</p>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon revenue">
                  <FaDollarSign />
                </div>
                <div className="stat-content">
                  <h3>${dashboardData?.totalRevenue?.toLocaleString()}</h3>
                  <p>Total Revenue</p>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon bbq">
                  <GiBarbecue />
                </div>
                <div className="stat-content">
                  <h3>{dashboardData?.bbqTeams}</h3>
                  <p>BBQ Teams</p>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon kids">
                  <FaBaby />
                </div>
                <div className="stat-content">
                  <h3>{dashboardData?.kidsEntries}</h3>
                  <p>Kids Entries</p>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon jackpot">
                  <FaTrophy />
                </div>
                <div className="stat-content">
                  <h3>{dashboardData?.jackpotEntries}</h3>
                  <p>Jackpot Entries</p>
                </div>
              </div>

              <div className="stat-card">
                <div className="stat-icon event">
                  <FaCalendarAlt />
                </div>
                <div className="stat-content">
                  <h3>{new Date(dashboardData?.eventDate).toLocaleDateString()}</h3>
                  <p>Event Date</p>
                </div>
              </div>
            </div>

            
            <div className="quick-actions">
              <h2>Quick Actions</h2>
              <div className="action-buttons">
                <button onClick={exportData} className="action-btn">
                  <FaDownload />
                  Export All Data
                </button>
                <button onClick={() => setActiveTab('registrations')} className="action-btn">
                  <FaEye />
                  View All Registrations
                </button>
                <button onClick={() => setActiveTab('payments')} className="action-btn">
                  <FaDollarSign />
                  Payment Summary
                </button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'registrations' && (
          <div className="registrations-section">
            
            <div className="table-controls">
              <div className="search-bar">
                <FaSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Search registrations..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              <div className="filter-group">
                <FaFilter className="filter-icon" />
                <select 
                  value={filterStatus} 
                  onChange={(e) => setFilterStatus(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="paid">Paid</option>
                  <option value="pending">Pending</option>
                  <option value="processing">Processing</option>
                  <option value="succeeded">Succeeded</option>
                </select>
              </div>

              <div className="items-per-page">
                <label>Show:</label>
                <select 
                  value={itemsPerPage} 
                  onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>

              <button onClick={exportData} className="export-btn">
                <FaDownload />
                Export
              </button>
            </div>

            
            <div className="table-info">
              <p>
                Showing {startIndex + 1} to {Math.min(endIndex, filteredRegistrations.length)} of {filteredRegistrations.length} registrations
              </p>
            </div>

            
            <div className="table-container">
              <table className="registrations-table">
                <thead>
                  <tr>
                    <th>Registration ID</th>
                    <th>Team/Participant</th>
                    <th>Contact</th>
                    <th>Type</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentPageRegistrations.map((reg) => (
                    <tr key={reg.id}>
                      <td>
                        <span className="reg-id">{reg.id}</span>
                      </td>
                      <td>
                        <div className="team-info">
                          <strong>{reg.teamName}</strong>
                          <br />
                          <small>{reg.headCookName}</small>
                        </div>
                      </td>
                      <td>
                        <div className="contact-info">
                          <div>{reg.email}</div>
                          <small>{reg.phone}</small>
                        </div>
                      </td>
                      <td>
                        <div className="registration-type">
                          {reg.bbqTeam && <span className="type-badge bbq">BBQ Team</span>}
                          {reg.kidsEntries.length > 0 && <span className="type-badge kids">Kids</span>}
                          {Object.values(reg.jackpotCategories).some(Boolean) && <span className="type-badge jackpot">Jackpot</span>}
                        </div>
                      </td>
                      <td>
                        <span className="amount">${reg.totalPayment}</span>
                      </td>
                      <td>
                        <span className={`status-badge ${reg.paymentStatus}`}>
                          {reg.paymentStatus.charAt(0).toUpperCase() + reg.paymentStatus.slice(1)}
                        </span>
                      </td>
                      <td>
                        <span className="date">
                          {new Date(reg.registrationDate).toLocaleDateString()}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button 
                            className="view-btn" 
                            title="View Details"
                            onClick={() => handleViewRegistration(reg)}
                          >
                            <FaEye />
                          </button>
                          <button 
                            className="delete-btn" 
                            title="Delete Registration"
                            onClick={() => handleDeleteRegistration(reg.id)}
                          >
                            <FaTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {filteredRegistrations.length === 0 && (
                <div className="no-results">
                  <p>No registrations found matching your criteria.</p>
                </div>
              )}
            </div>

            
            {totalPages > 1 && (
              <div className="pagination-container">
                <div className="pagination-info">
                  <p>Page {currentPage} of {totalPages}</p>
                </div>
                
                <div className="pagination-controls">
                  <button 
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="pagination-btn"
                  >
                    <FaChevronLeft />
                    Previous
                  </button>

                  <div className="page-numbers">
                    {[...Array(totalPages)].map((_, index) => {
                      const pageNumber = index + 1;
                      const isVisible = 
                        pageNumber === 1 || 
                        pageNumber === totalPages || 
                        (pageNumber >= currentPage - 2 && pageNumber <= currentPage + 2);
                      
                      if (!isVisible) {
                        
                        if (pageNumber === currentPage - 3 || pageNumber === currentPage + 3) {
                          return <span key={pageNumber} className="pagination-ellipsis">...</span>;
                        }
                        return null;
                      }

                      return (
                        <button
                          key={pageNumber}
                          onClick={() => handlePageChange(pageNumber)}
                          className={`page-number ${currentPage === pageNumber ? 'active' : ''}`}
                        >
                          {pageNumber}
                        </button>
                      );
                    })}
                  </div>

                  <button 
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="pagination-btn"
                  >
                    Next
                    <FaChevronRight />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'payments' && (
          <div className="payments-section">
            <div className="payment-summary">
              <h2>Payment Summary</h2>
              
              <div className="payment-stats">
                <div className="payment-stat">
                  <h3>${dashboardData?.totalRevenue?.toLocaleString()}</h3>
                  <p>Total Revenue</p>
                </div>
                <div className="payment-stat">
                  <h3>{dashboardData?.paidRegistrations}</h3>
                  <p>Paid Registrations</p>
                </div>
                <div className="payment-stat">
                  <h3>{dashboardData?.pendingRegistrations}</h3>
                  <p>Pending Payments</p>
                </div>
              </div>

              <div className="payment-breakdown">
                <h4>Revenue Breakdown</h4>
                <div className="breakdown-item">
                  <span>BBQ Team Registrations</span>
                  <span>${(dashboardData?.bbqTeams * 250)?.toLocaleString()}</span>
                </div>
                <div className="breakdown-item">
                  <span>Kids Entries</span>
                  <span>${(dashboardData?.kidsEntries * 25)?.toLocaleString()}</span>
                </div>
                <div className="breakdown-item">
                  <span>Jackpot Categories</span>
                  <span>${(dashboardData?.jackpotEntries * 25)?.toLocaleString()}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        
        {showModal && selectedRegistration && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Registration Details</h2>
                <div className="modal-header-actions">
                  {selectedRegistration.paymentStatus !== 'paid' && selectedRegistration.paymentStatus !== 'succeeded' && (
                    <button 
                      className="mark-paid-btn"
                      onClick={() => handleMarkAsPaid(selectedRegistration.id)}
                      title="Mark as Paid"
                    >
                      <FaCheck />
                      Mark as Paid
                    </button>
                  )}
                  {(selectedRegistration.paymentStatus === 'paid' || selectedRegistration.paymentStatus === 'succeeded') && (
                    <button 
                      className="mark-paid-btn disabled"
                      disabled
                      title="Already Paid"
                    >
                      <FaCheck />
                      Already Paid
                    </button>
                  )}
                  <button className="modal-close" onClick={closeModal}>
                    <FaTimes />
                  </button>
                </div>
              </div>
              
              <div className="modal-body">
                <div className="registration-details">
                  
                  <div className="detail-section">
                    <h3><FaUser className="section-icon" />Basic Information</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <label>Registration ID:</label>
                        <span className="reg-id">{selectedRegistration.id}</span>
                      </div>
                      <div className="detail-item">
                        <label>Team/Participant:</label>
                        <span>{selectedRegistration.teamName}</span>
                      </div>
                      <div className="detail-item">
                        <label>Head Cook/Contact:</label>
                        <span>{selectedRegistration.headCookName}</span>
                      </div>
                      <div className="detail-item">
                        <label>Registration Date:</label>
                        <span>{new Date(selectedRegistration.registrationDate).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>

                  
                  <div className="detail-section">
                    <h3><FaEnvelope className="section-icon" />Contact Information</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <label><FaEnvelope className="detail-icon" />Email:</label>
                        <span>{selectedRegistration.email}</span>
                      </div>
                      <div className="detail-item">
                        <label><FaPhone className="detail-icon" />Phone:</label>
                        <span>{selectedRegistration.phone}</span>
                      </div>
                      {selectedRegistration.address && (
                        <div className="detail-item full-width">
                          <label><FaHome className="detail-icon" />Address:</label>
                          <span>{selectedRegistration.address}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  
                  <div className="detail-section">
                    <h3><GiBarbecue className="section-icon" />Competition Details</h3>
                    <div className="competition-summary">
                      {selectedRegistration.bbqTeam && (
                        <div className="competition-item">
                          <span className="type-badge bbq">BBQ Team</span>
                          <span className="competition-details">
                            Pit Type: {selectedRegistration.pitType || 'N/A'} | 
                            Team Members: {selectedRegistration.teamMembers || 'N/A'}
                          </span>
                        </div>
                      )}
                      
                      {Object.entries(selectedRegistration.jackpotCategories || {})
                        .filter(([_, value]) => value)
                        .map(([category, _]) => (
                          <div key={category} className="competition-item">
                            <span className="type-badge jackpot">Jackpot</span>
                            <span className="competition-details">
                              {category === 'panDeCampo' ? 'Pan de Campo' : 
                               category.charAt(0).toUpperCase() + category.slice(1)}
                            </span>
                          </div>
                        ))}
                    </div>
                  </div>

                  
                  {selectedRegistration.kidsEntries && selectedRegistration.kidsEntries.length > 0 && (
                    <div className="detail-section">
                      <h3><FaChild className="section-icon" />Kids Entries ({selectedRegistration.kidsEntries.length})</h3>
                      <div className="kids-list">
                        {selectedRegistration.kidsEntries.map((kid, index) => (
                          <div key={index} className="kid-item">
                            <div className="kid-info">
                              <strong>{kid.childName}</strong>
                              <span className="kid-details">
                                Age: {kid.childAge} | Division: {kid.ageDivision || 'N/A'}
                              </span>
                            </div>
                            {kid.allergies && (
                              <div className="kid-allergies">
                                <small>Allergies: {kid.allergies}</small>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  
                  <div className="detail-section">
                    <h3><FaDollarSign className="section-icon" />Payment Information</h3>
                    <div className="payment-info">
                      <div className="payment-summary-modal">
                        <div className="payment-amount">
                          <span>Total Amount:</span>
                          <span className="amount">${selectedRegistration.totalPayment}</span>
                        </div>
                        <div className="payment-status-container">
                          <span>Status:</span>
                          <span className={`status-badge ${selectedRegistration.paymentStatus}`}>
                            {selectedRegistration.paymentStatus.charAt(0).toUpperCase() + selectedRegistration.paymentStatus.slice(1)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  
                  {selectedRegistration.comments && (
                    <div className="detail-section">
                      <h3>Comments</h3>
                      <div className="comments-box">
                        <p>{selectedRegistration.comments}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modern Confirmation Modal */}
        {showConfirmModal && confirmData && (
          <div className="modal-overlay" onClick={closeConfirmModal}>
            <div className="confirm-modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="confirm-modal-header">
                <div className={`confirm-icon ${confirmData.confirmClass}`}>
                  {confirmData.confirmClass === 'danger' ? <FaTrash /> : <FaCheck />}
                </div>
                <h2>{confirmData.title}</h2>
              </div>
              
              <div className="confirm-modal-body">
                <p className="confirm-message">{confirmData.message}</p>
                {confirmData.details && (
                  <div className="confirm-details">
                    {confirmData.details.split('\n').map((line, index) => (
                      <p key={index}>{line}</p>
                    ))}
                  </div>
                )}
                {confirmData.warning && (
                  <div className="confirm-warning">
                    <p>{confirmData.warning}</p>
                  </div>
                )}
              </div>
              
              <div className="confirm-modal-actions">
                <button 
                  className="confirm-btn cancel"
                  onClick={closeConfirmModal}
                >
                  Cancel
                </button>
                <button 
                  className={`confirm-btn ${confirmData.confirmClass}`}
                  onClick={handleConfirmAction}
                >
                  {confirmData.confirmText}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard; 